'use client';
import { Languages } from '@/constant/Language';
import { explorerAnalytics } from '@/lib/analytics';
import { pendoTrack } from '@/lib/pendo/trackPendoEvent';
import * as Select from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useContext, useRef, useState } from 'react';
import { useExplorer } from '@/hooks/useExplorer';
import { ClientAuthContext } from '@/auth/ClientAuthProvider';
import { twMerge } from 'tailwind-merge';

type SDKSelectorVariant = 'default' | 'subtle';

const triggerStyles: Record<
  SDKSelectorVariant,
  { base: string; disabled: string; enabled: string }
> = {
  default: {
    base: 'text-xs flex justify-center items-center gap-1 rounded-lg border-solid px-2 border-2',
    disabled:
      'border-gray-300 text-gray-400 cursor-not-allowed opacity-50 bg-gray-50',
    enabled: 'border-black text-black hover:bg-gray-50',
  },
  subtle: {
    base: 'min-w-[95px] rounded-xl bg-transparent border-none shadow-none flex items-center gap-2 font-bold text-xs p-2',
    disabled: 'cursor-not-allowed text-gray-400 opacity-50',
    enabled: 'cursor-pointer text-black hover:bg-[#eaf5f7]',
  },
};

const SDKSelector = ({
  selectedSDK,
  onSelectSDK,
  disabled = false,
  variant = 'default',
  triggerSource,
}: {
  selectedSDK: string;
  onSelectSDK: (sdk: string) => void;
  disabled?: boolean;
  variant?: SDKSelectorVariant;
  triggerSource?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const prevSDKRef = useRef(selectedSDK);
  const styles = triggerStyles[variant];
  const { endpoint } = useExplorer();
  const { isLoggedIn } = useContext(ClientAuthContext);

  const handleValueChange = (newSDK: string) => {
    explorerAnalytics.sdkChanged({
      fromLanguage: prevSDKRef.current,
      toLanguage: newSDK,
      triggerSource: triggerSource ?? 'playground',
      resource: endpoint?.name,
      endpoint: endpoint?.id,
      isLoggedIn: !!isLoggedIn,
    });
    pendoTrack('sdk_language_switched', {
      from_language: prevSDKRef.current,
      to_language: newSDK,
      resource: endpoint?.name,
      endpoint: endpoint?.id,
    });
    prevSDKRef.current = newSDK;
    onSelectSDK(newSDK);
  };

  return (
    <Select.Root
      value={selectedSDK}
      onValueChange={handleValueChange}
      disabled={disabled}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Select.Trigger
        className={twMerge(
          styles.base,
          disabled ? styles.disabled : styles.enabled
        )}
        disabled={disabled}
      >
        <Select.Value>
          <span className="font-bold">
            {selectedSDK
              ? Languages.find((lang) => lang.key === selectedSDK)?.name
              : 'Select SDK'}
          </span>
        </Select.Value>
        <Select.Icon>
          {isOpen ? (
            <ChevronUp
              className={twMerge('h-4 w-4', disabled && 'text-gray-400')}
            />
          ) : (
            <ChevronDown
              className={twMerge('h-4 w-4', disabled && 'text-gray-400')}
            />
          )}
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className="overflow-hidden bg-white/90 backdrop-blur-sm rounded-md shadow-lg ring-1 ring-black/5 w-[180px]"
          position="popper"
        >
          <Select.Viewport className="py-1">
            {Languages.map((lang) => (
              <Select.Item
                key={lang.key}
                value={lang.key}
                className={twMerge(
                  'flex items-center justify-between w-full px-4 py-2 text-sm text-left hover:bg-[#F8F9FA] outline-none cursor-pointer',
                  selectedSDK === lang.key &&
                    'bg-gradient-to-r from-shade-red-500/10 to-shade-red-800/10'
                )}
              >
                <Select.ItemText>
                  <span
                    className={
                      selectedSDK === lang.key
                        ? 'bg-gradient-to-r from-shade-red-500 to-shade-red-800 bg-clip-text text-transparent font-medium'
                        : ''
                    }
                  >
                    {lang.name}
                  </span>
                </Select.ItemText>
                <Select.ItemIndicator>
                  <Check className="h-4 w-4 text-shade-red-600" />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

export default SDKSelector;
